export type PcgReportMode = 'create' | 'edit'

export type PcgReportStatus = 'draft' | 'final' | 'inReview' | 'approved'

export type PcgReportStatusTone = 'neutral' | 'info' | 'success' | 'warning' | 'danger' | 'muted'

export type PcgReportGenerationJobAction = 'preview' | 'generate'

export type PcgReportGenerationJobStatus =
  | 'queued'
  | 'inprogress'
  | 'completed'
  | 'failed'
  | 'expired'

export interface PcgReportGenerationJob {
  jobId: string
  reportId: string
  action: PcgReportGenerationJobAction
  status: PcgReportGenerationJobStatus
  progressPercent: number
  stage: string
  errorMessage?: string
  fileName?: string
  contentType?: string
  fileSize?: number
  startedAt?: string
  completedAt?: string
  expiresAt?: string
}

export interface PcgReportMeta {
  id: string
  projectId: string
  projectName: string
  mode: PcgReportMode
  status: PcgReportStatus
  statusLabel: string
  documentStatus: PcgReportStatus
  reportNumber?: string
  reportNamingConvention?: string
  referenceSequenceNumber?: string
  dateOfIssue?: string
  lastUpdated?: string
}

export interface PcgReportSummary {
  id: string
  reportNumber: string
  dateOfIssue?: string
  statusLabel: string
  statusTone?: PcgReportStatusTone
  status?: PcgReportStatus
  matchedItem?: string
  matchedItemCount?: number
  createdAt?: string
  updatedAt?: string
}

export interface BudgetLineItem {
  id: string
  parentId?: string | null
  itemNumber: string
  title: string
  section?: 'main' | 'summary' | 'header'
  initialBudget?: number | null
  budgetTransfers?: number | null
  adjustedBudget?: number | null
  anticipated?: number | null
  endCost?: number | null
  variance?: number | null
  committed?: number | null
  nonCommitted?: number | null
  expenditure?: number | null
  costToComplete?: number | null
  isTotalRow?: boolean
  children?: BudgetLineItem[]
}

export interface VariationLineItem {
  id: string
  variationAssessmentId?: string
  parentId?: string | null
  variationNumber: string
  description: string
  statusAmounts?: Record<string, number | null>
  payment?: number | null
  maximumPaymentValue?: number | null
  fundingSource?: string
  statusLabel?: string
  statusTone?: PcgReportStatusTone
  cause?: string
  notes?: string
  isTotalRow?: boolean
  children?: VariationLineItem[]
}

export interface ProvisionalSumRegisterLineItem {
  id: string
  variationAssessmentId?: string
  parentId?: string | null
  variationNumber: string
  description: string
  statusAmounts?: Record<string, number | null>
  payment?: number | null
  maximumPaymentValue?: number | null
  fundingSource?: string
  statusLabel?: string
  statusTone?: PcgReportStatusTone
  approvedInitialValue?: number | null
  approvedCommitment?: number | null
  uncommitted?: number | null
  expenditure?: number | null
  remainingProvisionalSum?: number | null
  comments?: string
  isTotalRow?: boolean
  children?: ProvisionalSumRegisterLineItem[]
}

export interface BudgetTransferLine {
  id: string
  parentId?: string | null
  date?: string
  description?: string
  amount?: number | null
  from?: string
  to?: string
  isTotalRow?: boolean
  children?: BudgetTransferLine[]
}

export interface CashflowMonthlyLine {
  id: string
  parentId?: string | null
  month: string
  forecastMonthly?: number | null
  forecastCumulative?: number | null
  actualMonthly?: number | null
  actualCumulative?: number | null
  isAnnual?: boolean
  isTotalRow?: boolean
  children?: CashflowMonthlyLine[]
}

export interface CashflowAnnualLine {
  id: string
  parentId?: string | null
  year: string
  forecastAnnual?: number | null
  forecastCumulative?: number | null
  actualAnnual?: number | null
  actualCumulative?: number | null
  isTotalRow?: boolean
  children?: CashflowAnnualLine[]
}

export interface ContingencyPoint {
  label: string
  actualContingency?: number
  forecastContingency?: number
  actualExpenditure?: number
  forecastExpenditure?: number
}

export interface ContingencyTableRow {
  id: string
  dateLabel: string
  percentage?: number | null
}

export interface PcgContingencyHistoryPoint {
  month: string
  reportId?: string | null
  reportNumber?: string | null
  dateOfIssue?: string | null
  contingencyAdjustedBudget?: number | null
  contingencyUncommitted?: number | null
}

export type PcgReportBuilderSectionKind = 'cover' | 'toc' | 'content'

export interface PcgReportBuilderSection {
  id: string
  title: string
  kind: PcgReportBuilderSectionKind
  order: number
  contentHtml: string
  isAuto?: boolean
  readOnly?: boolean
  isLandscape?: boolean
  pageBreakAfter?: boolean
  isHidden?: boolean
}

export interface PcgReportApproval {
  name?: string | null
  role?: string | null
  date?: string | null
  signatureFileName?: string | null
}

export interface PcgReport {
  meta: PcgReportMeta
  documentStatus: PcgReportStatus
  projectStartDate?: string
  programCompletionDate?: string
  constructionStartDate?: string
  constructionPracticalCompletionDate?: string
  budget: BudgetLineItem[]
  variations: VariationLineItem[]
  provisionalSumRegister: ProvisionalSumRegisterLineItem[]
  budgetTransfers: BudgetTransferLine[]
  cashflowMonthly: CashflowMonthlyLine[]
  cashflowAnnual: CashflowAnnualLine[]
  constructionCashflowMonthly?: CashflowMonthlyLine[]
  constructionCashflowAnnual?: CashflowAnnualLine[]
  contingencySeries: ContingencyPoint[]
  contingencyTable: ContingencyTableRow[]
  contingencyHistory?: PcgContingencyHistoryPoint[]
  reportBuilderNotes?: string
  reportBuilderSections?: PcgReportBuilderSection[]
  approval?: PcgReportApproval
  preparedBy?: PcgReportApproval
}

export const DEFAULT_PCG_REPORT_META: PcgReportMeta = {
  id: 'PCG-DRAFT',
  projectId: 'PROJECT-001',
  projectName: 'New Project',
  mode: 'create',
  status: 'draft',
  statusLabel: 'Draft',
  documentStatus: 'draft',
  reportNamingConvention: 'PCG',
  referenceSequenceNumber: '001',
}

export const DEFAULT_PCG_REPORT: PcgReport = {
  meta: DEFAULT_PCG_REPORT_META,
  documentStatus: 'draft',
  projectStartDate: undefined,
  programCompletionDate: undefined,
  constructionStartDate: undefined,
  constructionPracticalCompletionDate: undefined,
  budget: [],
  variations: [],
  provisionalSumRegister: [],
  budgetTransfers: [],
  cashflowMonthly: [],
  cashflowAnnual: [],
  constructionCashflowMonthly: [],
  constructionCashflowAnnual: [],
  contingencySeries: [],
  contingencyTable: [],
  contingencyHistory: [],
  reportBuilderNotes: '',
  reportBuilderSections: buildDefaultReportBuilderSections(),
}

export function buildDefaultReportBuilderSections(): PcgReportBuilderSection[] {
  return [
    {
      id: 'cover',
      title: 'Cover Page',
      kind: 'cover',
      order: 0,
      pageBreakAfter: true,
      contentHtml: `
        <div class="pcg-cover__title">{{Project Name}}</div>
        <div class="pcg-cover__subtitle">Quantity Surveyor's Report</div>
        <div class="pcg-cover__meta">
          <div>{{PCG No}}</div>
          <div>{{Current Date}}</div>
        </div>
        <div class="pcg-cover__footer">
          <div>{{Project Director Full Name}}</div>
          <div>{{Project Director Role}}</div>
          <div>{{Organisation}}</div>
          <div>{{Organisation Address}}</div>
          <div>{{Organisation Phone}}</div>
          <div>{{Organisation Website}}</div>
        </div>
      `,
    },
    {
      id: 'toc',
      title: 'Table of Contents',
      kind: 'toc',
      isAuto: true,
      readOnly: true,
      order: 1,
      pageBreakAfter: true,
      contentHtml: '',
    },
    {
      id: 'project-team',
      title: 'Project Team',
      kind: 'content',
      order: 2,
      pageBreakAfter: true,
      contentHtml: `
        <h2>PROJECT TEAM</h2>
        <p><strong>Client:</strong> {{Client Name}}</p>
        <p><strong>Project Manager:</strong> {{Project Manager Name}}</p>
        <p><strong>Cost Manager:</strong> {{Cost Manager Name}}</p>
        <p><strong>Architect:</strong> {{Architect Name}}</p>
        <p><strong>Structural & Civil Engineer:</strong> {{Structural Engineer Name}}</p>
        <p><strong>Services Engineer:</strong> {{Services Engineer Name}}</p>
        <p><strong>Landscape Architects:</strong> {{Landscape Architect Name}}</p>
        <p><strong>ESD:</strong> {{ESD Consultant Name}}</p>
      `,
    },
    {
      id: 'overview',
      title: 'Overview',
      kind: 'content',
      order: 3,
      isAuto: true,
      readOnly: true,
      pageBreakAfter: true,
      contentHtml: `
        <h2 style="color: #f79421;">OVERVIEW</h2>
        <div class="pcg-report-builder__overview">
          <div class="pcg-report-builder__overview-card">
            <div class="pcg-report-builder__overview-card-header">
              <div class="pcg-report-builder__overview-card-title">All Variations</div>
              <span class="pcg-report-builder__overview-count">{{All Variations Count}}</span>
            </div>
            <div class="pcg-report-builder__overview-card-body">
              {{All Variations Chart}}
            </div>
          </div>
          <div class="pcg-report-builder__overview-card">
            <div class="pcg-report-builder__overview-card-header">
              <div>
                <div class="pcg-report-builder__overview-card-title">Remaining Contingency</div>
                <div class="pcg-report-builder__overview-card-subtitle">Contingency remaining for {{Project Name}}</div>
              </div>
            </div>
            <div class="pcg-report-builder__overview-card-body">
              {{Remaining Contingency Summary}}
            </div>
          </div>
        </div>
      `,
    },
    {
      id: 'executive-summary',
      title: 'Executive Summary',
      kind: 'content',
      order: 4,
      pageBreakAfter: true,
      contentHtml: `
          <h2 style="color: #f79421;">1 EXECUTIVE SUMMARY</h2>
          <p>
            Donald Cant Watts Corke has been appointed by {{Client Name}} as Quantity Surveyors for the delivery of
            {{Project Name}} at {{Programme Details - Street Address}} {{Programme Details - City}},
            {{Programme Details - State/Province}}.
          </p>
        `,
    },
    {
      id: 'introduction',
      title: 'Introduction',
      kind: 'content',
      order: 5,
      pageBreakAfter: true,
      contentHtml: `
          <h2 style="color: #f79421;">2 INTRODUCTION</h2>
          <p>
            Donald Cant Watts Corke has been appointed by {{Client Name}} as Quantity Surveyors for the delivery of
            {{Project Name}} at {{Programme Details - Street Address}} {{Programme Details - City}},
            {{Programme Details - State/Province}}.
          </p>
        `,
    },
    {
      id: 'current-activities',
      title: 'Current Activities',
      kind: 'content',
      order: 6,
      pageBreakAfter: true,
      contentHtml: `
          <h2 style="color: #f79421;">3 CURRENT ACTIVITIES</h2>
          <p>Donald Cant Watts Corke has undertaken the following activities in the past month:</p>
          <ul>
            <li>&nbsp;</li>
            <li>&nbsp;</li>
            <li>&nbsp;</li>
          </ul>
          <p>The activities forecast for the month ahead are:</p>
          <ul>
            <li>&nbsp;</li>
            <li>&nbsp;</li>
            <li>&nbsp;</li>
          </ul>
        `,
    },
    {
      id: 'project-summary',
      title: 'Project Summary',
      kind: 'content',
      order: 7,
      isAuto: true,
      readOnly: true,
      isLandscape: true,
      pageBreakAfter: true,
      contentHtml: `
          <h2 style="color: #f79421;">4 PROJECT SUMMARY</h2>
          {{Project Summary Table}}
        `,
    },
    {
      id: 'variations-summary',
      title: 'Variations',
      kind: 'content',
      order: 8,
      isAuto: true,
      readOnly: true,
      isLandscape: false,
      pageBreakAfter: true,
      contentHtml: `
          <h2 style="color: #f79421;">VARIATIONS</h2>
          <div class="pcg-report-builder__variations-summary">
            <div class="pcg-report-builder__variations-summary-item">
              <div class="pcg-report-builder__variations-summary-title">
                Recommended
              </div>
              <div class="pcg-report-builder__variations-summary-status-text">{{Variations Recommended Status Text}}</div>
              {{Variations Recommended Table}}
            </div>
            <div class="pcg-report-builder__variations-summary-item">
              <div class="pcg-report-builder__variations-summary-title">
                Submitted
              </div>
              <div class="pcg-report-builder__variations-summary-status-text">{{Variations Submitted Status Text}}</div>
              {{Variations Submitted Table}}
            </div>
            <div class="pcg-report-builder__variations-summary-item">
              <div class="pcg-report-builder__variations-summary-title">
                Estimated
              </div>
              <div class="pcg-report-builder__variations-summary-status-text">{{Variations Estimated Status Text}}</div>
              {{Variations Estimated Table}}
            </div>
          </div>
        `,
    },
    {
      id: 'provisional-sums-summary',
      title: 'Provisional Sums',
      kind: 'content',
      order: 9,
      isAuto: true,
      readOnly: true,
      isLandscape: true,
      pageBreakAfter: true,
      contentHtml: `
          <h2 style="color: #f79421;">PROVISIONAL SUMS</h2>
          <div class="pcg-report-builder__variations-summary-item">
            {{Provisional Sums Forecast Table}}
          </div>
        `,
    },
    {
      id: 'contingency-erosion',
      title: 'Contingency Erosion',
      kind: 'content',
      order: 10,
      isAuto: true,
      readOnly: true,
      pageBreakAfter: false,
      contentHtml: `
          <h2 style="color: #f79421;">6 CONTINGENCY EROSION</h2>
          <div class="pcg-report-builder__contingency-charts">
            <div class="pcg-report-builder__contingency-card">
              <div class="pcg-report-builder__contingency-chart-title">Expenditure vs Contingency</div>
              <div class="pcg-report-builder__contingency-card-body">
                {{Contingency Expenditure Chart}}
              </div>
            </div>
            <div class="pcg-report-builder__contingency-card">
              <div class="pcg-report-builder__contingency-chart-title">Contingency</div>
              <div class="pcg-report-builder__contingency-card-body">
                {{Contingency Cumulative Chart}}
              </div>
            </div>
            <div class="pcg-report-builder__contingency-card pcg-report-builder__contingency-percentage-card">
              <div class="pcg-report-builder__contingency-chart-title">Percentage</div>
              <div class="pcg-report-builder__contingency-card-body">
                {{Contingency Remaining Percentage Table}}
              </div>
            </div>
          </div>
        `,
    },
    {
      id: 'appendices',
      title: 'Appendices',
      kind: 'content',
      order: 11,
      pageBreakAfter: true,
      contentHtml: `
          <h2 style="color: #f79421;">APPENDICES</h2>
        `,
    },
    {
      id: 'variations',
      title: 'Variations',
      kind: 'content',
      order: 12,
      isAuto: true,
      readOnly: true,
      isLandscape: true,
      pageBreakAfter: true,
      contentHtml: `
          <h2 style="color: #f79421;">5 VARIATIONS</h2>
          {{Variations Table}}
        `,
    },
    {
      id: 'provisional-sums',
      title: 'Provisional Sums',
      kind: 'content',
      order: 13,
      isAuto: true,
      readOnly: true,
      isLandscape: true,
      pageBreakAfter: true,
      contentHtml: `
          <h2 style="color: #f79421;">7 PROVISIONAL SUMS</h2>
          <p>The total provisional sum allowance is {{Provisional Sum Total}}.</p>
          {{Provisional Sum Register Table}}
        `,
    },
    {
      id: 'budget-transfer',
      title: 'Budget Transfer',
      kind: 'content',
      order: 14,
      isAuto: true,
      readOnly: true,
      pageBreakAfter: false,
      contentHtml: `
          <h2 style="color: #f79421;">8 BUDGET TRANSFER</h2>
          <p>{{Budget Transfers Summary}}</p>
          {{Budget Transfers Table}}
        `,
    },
    {
      id: 'project-cash-flow',
      title: 'Project Cash Flow',
      kind: 'content',
      order: 15,
      isAuto: true,
      readOnly: true,
      isLandscape: true,
      pageBreakAfter: true,
      contentHtml: `
          <h2 style="color: #f79421;">9 PROJECT CASH FLOW</h2>
          <div class="pcg-report-builder__cashflow-dates">
            <div class="pcg-report-builder__cashflow-date">
              <div class="pcg-report-builder__cashflow-date-label">Program Start Date</div>
              <div class="pcg-report-builder__cashflow-date-value">{{Project Start Date}}</div>
            </div>
            <div class="pcg-report-builder__cashflow-date">
              <div class="pcg-report-builder__cashflow-date-label">Program Completion Date</div>
              <div class="pcg-report-builder__cashflow-date-value">{{Program Completion Date}}</div>
            </div>
          </div>
          <div class="pcg-report-builder__cashflow-layout">
            <div class="pcg-report-builder__cashflow-tables">
              <div class="pcg-report-builder__cashflow-card">
                <div class="pcg-report-builder__cashflow-card-title">Monthly Cash Flow</div>
                {{Cashflow Monthly Table}}
              </div>
              <div class="pcg-report-builder__cashflow-card">
                <div class="pcg-report-builder__cashflow-card-title">Annual Summary</div>
                {{Cashflow Annual Table}}
              </div>
            </div>
            <div class="pcg-report-builder__cashflow-charts">
              <div class="pcg-report-builder__cashflow-card">
                <div class="pcg-report-builder__cashflow-card-title">Monthly Cash Flow</div>
                {{Cashflow Monthly Chart}}
              </div>
              <div class="pcg-report-builder__cashflow-card">
                <div class="pcg-report-builder__cashflow-card-title">Cumulative Cash Flow</div>
                {{Cashflow Cumulative Chart}}
              </div>
            </div>
          </div>
        `,
    },
    {
      id: 'construction-cash-flow',
      title: 'Construction Cash Flow',
      kind: 'content',
      order: 16,
      isAuto: true,
      readOnly: true,
      isLandscape: true,
      pageBreakAfter: true,
      contentHtml: `
          <h2 style="color: #f79421;">10 CONSTRUCTION CASH FLOW</h2>
          <div class="pcg-report-builder__cashflow-dates">
            <div class="pcg-report-builder__cashflow-date">
              <div class="pcg-report-builder__cashflow-date-label">Construction Start Date</div>
              <div class="pcg-report-builder__cashflow-date-value">{{Construction Start Date}}</div>
            </div>
            <div class="pcg-report-builder__cashflow-date">
              <div class="pcg-report-builder__cashflow-date-label">Construction Date for Practical Completion</div>
              <div class="pcg-report-builder__cashflow-date-value">{{Construction Date for Practical Completion}}</div>
            </div>
          </div>
          <div class="pcg-report-builder__cashflow-layout">
            <div class="pcg-report-builder__cashflow-tables">
              <div class="pcg-report-builder__cashflow-card">
                <div class="pcg-report-builder__cashflow-card-title">Monthly Cash Flow</div>
                {{Construction Cashflow Monthly Table}}
              </div>
              <div class="pcg-report-builder__cashflow-card">
                <div class="pcg-report-builder__cashflow-card-title">Annual Summary</div>
                {{Construction Cashflow Annual Table}}
              </div>
            </div>
            <div class="pcg-report-builder__cashflow-charts">
              <div class="pcg-report-builder__cashflow-card">
                <div class="pcg-report-builder__cashflow-card-title">Monthly Cash Flow</div>
                {{Construction Cashflow Monthly Chart}}
              </div>
              <div class="pcg-report-builder__cashflow-card">
                <div class="pcg-report-builder__cashflow-card-title">Cumulative Cash Flow</div>
                {{Construction Cashflow Cumulative Chart}}
              </div>
            </div>
          </div>
        `,
    },
    {
      id: 'expenditure',
      title: 'Expenditure',
      kind: 'content',
      order: 17,
      pageBreakAfter: false,
      contentHtml: `
          <h2 style="color: #f79421;">11 EXPENDITURE</h2>
          <p>
            The current expenditure for the project is (value), which consists of Construction expenditure (from DCWC recommendations) only.
            We have not been advised of Other Cost centre expenditure.
          </p>
        `,
    },
  ]
}
